// Cognito-backed drop-in for the @supabase/supabase-js auth surface the app
// actually uses (see scripts/migrate-auth-to-cognito/baseline/fe_auth_calls.txt).
// AWS mode only (NUXT_PUBLIC_AWS_AUTH_ENABLED). The session object mimics the
// Supabase Session shape; user.id = the `profile_id` claim injected by the
// pre-token Lambda (== profiles.id, the app's identity everywhere).
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js'

export interface AwsSession {
  access_token: string
  refresh_token: string
  expires_at: number
  expires_in: number
  token_type: 'bearer'
  user: AwsUser
}

export interface AwsUser {
  id: string
  email: string
  aud: string
  role: string
  app_metadata: Record<string, unknown>
  user_metadata: Record<string, unknown>
  created_at: string
}

type AuthChangeCb = (event: string, session: AwsSession | null) => void

const UNSUPPORTED = (method: string) => ({
  data: { user: null, session: null },
  error: new Error(`[aws-mode] supabase.auth.${method} no soportado (flujo diferido a F5)`),
})

/**
 * Borra del dispositivo TODA sesión residual, no solo la del usuario que se está
 * yendo. Dos fugas reales, encontradas en staging el 2026-08-07:
 *
 * 1. `CognitoUser.signOut()` limpia únicamente las llaves del `LastAuthUser`. Si
 *    antes entró alguien más en ese navegador, su `idToken`, `accessToken` y
 *    —lo grave— su `refreshToken` se quedan en localStorage. Con el refresh
 *    token se piden tokens nuevos: en una recepción compartida eso alcanza para
 *    volver a entrar como el usuario anterior.
 *
 * 2. Las cookies `sb-<ref>-auth-token*` del proyecto Supabase abandonado
 *    sobreviven al logout, y el cliente SSR las refresca en la siguiente carga
 *    contra un proyecto que ya nadie audita, devolviendo una sesión viva.
 *
 * Es best-effort a propósito: si algo falla aquí, el logout debe completarse
 * igual. Cerrar sesión nunca puede quedarse a medias por no poder borrar basura.
 */
export function purgeResidualSessions(clientId: string) {
  if (typeof window === 'undefined') return
  try {
    const prefix = `CognitoIdentityServiceProvider.${clientId}.`
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(prefix)) localStorage.removeItem(k)
    }
  } catch { /* storage bloqueado (modo privado, cookies off) */ }

  purgeSupabaseCookies()
}

/**
 * Solo las cookies del proyecto Supabase abandonado. Va aparte de
 * `purgeResidualSessions` porque esto SÍ se puede correr al arrancar la app en
 * modo AWS —no toca la sesión de Cognito del usuario que está entrando—,
 * mientras que borrar las llaves de Cognito en el arranque lo dejaría afuera en
 * cada carga.
 */
export function purgeSupabaseCookies() {
  if (typeof window === 'undefined') return
  try {
    for (const raw of document.cookie.split(';')) {
      const name = raw.trim().split('=')[0]
      if (!name?.startsWith('sb-')) continue
      // Sin saber con qué path/domain se escribió, se borra en las variantes que
      // usa el cliente SSR de Supabase.
      const base = `${name}=; Max-Age=0; path=/`
      document.cookie = base
      document.cookie = `${base}; domain=${location.hostname}`
      document.cookie = `${base}; domain=.${location.hostname}`
    }
  } catch { /* idem */ }
}

/**
 * La otra mitad del barrido: las llaves `sb-*` en localStorage. El cliente de
 * @nuxtjs/supabase guarda la sesión en cookies cuando `useSsrCookies` está
 * activo, pero deja llaves en localStorage cuando no lo está y en instalaciones
 * viejas — así que hay que borrar las dos.
 *
 * Existe para poder retirar la sesión del proyecto Supabase abandonado SIN
 * llamar a `auth.signOut({ scope: 'local' })`. Ese signOut hace exactamente esta
 * misma limpieza, pero además emite `SIGNED_OUT` y GoTrue lo DIFUNDE por
 * `BroadcastChannel` a todas las demás pestañas del mismo origen (G-276).
 */
export function purgeSupabaseLocalStorage() {
  if (typeof window === 'undefined') return
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('sb-')) localStorage.removeItem(k)
    }
  } catch { /* storage bloqueado (modo privado, cookies off) */ }
}

/**
 * Retira por completo la sesión del proyecto Supabase abandonado (cookies +
 * localStorage) sin tocar la sesión de Cognito y sin emitir eventos de auth.
 * Es el reemplazo directo de `auth.signOut({ scope: 'local' })` al arrancar en
 * modo AWS. Ver G-276.
 */
export function purgeAbandonedSupabaseSession() {
  purgeSupabaseCookies()
  purgeSupabaseLocalStorage()
}

export function createCognitoAuth(opts: { poolId: string, clientId: string, functionsUrl?: string }) {
  const pool = new CognitoUserPool({ UserPoolId: opts.poolId, ClientId: opts.clientId })
  const listeners = new Set<AuthChangeCb>()

  const emit = (event: string, session: AwsSession | null) => {
    listeners.forEach(cb => { try { cb(event, session) } catch { /* listener errors are theirs */ } })
  }

  const toSession = (cs: CognitoUserSession): AwsSession => {
    const id = cs.getIdToken()
    const p = id.payload as Record<string, any>
    let appMeta: Record<string, unknown> = {}
    if (typeof p.app_metadata === 'string') {
      try { appMeta = JSON.parse(p.app_metadata) } catch { appMeta = {} }
    } else if (p.app_metadata) {
      appMeta = p.app_metadata
    }
    return {
      access_token: id.getJwtToken(),
      refresh_token: cs.getRefreshToken().getToken(),
      expires_at: p.exp,
      expires_in: Math.max(0, p.exp - Math.floor(Date.now() / 1000)),
      token_type: 'bearer',
      user: {
        // act_as_profile_id (user-scope impersonation claim) takes precedence —
        // mirrors auth.uid() on RDS so the FE identity matches what RLS sees.
        id: p.act_as_profile_id ?? p.profile_id ?? p.sub,
        email: p.email ?? '',
        aud: 'authenticated',
        role: 'authenticated',
        app_metadata: appMeta,
        user_metadata: {},
        created_at: '',
      },
    }
  }

  // Resultado interno de leer la sesión. `transient` separa "no hay sesión"
  // (el usuario se fue: no hay CognitoUser, o el refresh token ya no sirve) de
  // "no se pudo comprobar ahora" (red caída, Cognito throttling). Tratar las dos
  // igual era el bug: un parpadeo de red se volvía un logout.
  type SessionRead = { session: AwsSession | null, transient: boolean }

  // `getSession()` es callback-based: si la red se queda colgada puede no llamar
  // NUNCA de vuelta. Con el single-flight de abajo eso sería fatal —la promesa
  // compartida no se resolvería y TODA petición de token posterior quedaría
  // esperando para siempre—, así que la lectura lleva su propio tope y un cuelgue
  // se degrada a "transitorio" (se conserva la última sesión buena).
  const READ_TIMEOUT_MS = 10_000

  // Margen de expiración. `CognitoUserSession.isValid()` compara contra `exp`
  // PELADO —cero margen, a diferencia de los 30 s de supabase-js—, así que un
  // token se sigue mandando hasta el segundo exacto en que vence y una petición
  // firmada en ese filo llega expirada al otro lado. Cuando falta menos que esto
  // se renueva por adelantado. G-277.
  const EXPIRY_MARGIN_S = 90

  // Reintentos SOLO del arranque (ver `currentSession`). Topes cortos a
  // propósito: esta lectura bloquea la hidratación del plugin, así que la cota
  // del peor caso con la red muerta es READ_TIMEOUT_MS + 2×(300 ms + 3 s).
  const BOOT_RETRIES = 2
  const BOOT_RETRY_DELAY_MS = 300
  const BOOT_RETRY_TIMEOUT_MS = 3_000

  const secondsToExpiry = (cs: CognitoUserSession): number =>
    ((cs.getIdToken().payload as Record<string, any>).exp ?? 0) - Math.floor(Date.now() / 1000)

  const readSession = (timeoutMs = READ_TIMEOUT_MS): Promise<SessionRead> => new Promise((resolve) => {
    const u = pool.getCurrentUser()
    // Sin usuario guardado no hay nada que refrescar: es un logout de verdad.
    if (!u) return resolve({ session: null, transient: false })

    let settled = false
    const done = (r: SessionRead) => { if (!settled) { settled = true; resolve(r) } }
    const timer = setTimeout(() => done({ session: null, transient: true }), timeoutMs)

    u.getSession((err: Error | null, cs: CognitoUserSession | null) => {
      // getSession() refresca solo cuando el id/access token expiró; ese refresh
      // sale a la red, así que `err` puede ser un 429/timeout perfectamente
      // recuperable. NO es prueba de que la sesión murió.
      if (err) { clearTimeout(timer); return done({ session: null, transient: true }) }
      if (!cs || !cs.isValid()) { clearTimeout(timer); return done({ session: null, transient: false }) }

      // Token válido pero a punto de vencer: renovar YA, para no entregar uno que
      // expira en vuelo. Si esa renovación falla, el token viejo TODAVÍA sirve
      // unos segundos — se devuelve, NO se degrada a transitorio.
      if (secondsToExpiry(cs) > EXPIRY_MARGIN_S) {
        clearTimeout(timer)
        return done({ session: toSession(cs), transient: false })
      }
      u.refreshSession(cs.getRefreshToken(), (rErr: Error | null, refreshed: CognitoUserSession | null) => {
        clearTimeout(timer)
        done({ session: toSession(refreshed && !rErr ? refreshed : cs), transient: false })
      })
    })
  })

  // Última sesión buena. Sirve para dos cosas: detectar que el token CAMBIÓ
  // (→ TOKEN_REFRESHED) y no degradar a `null` ante un fallo transitorio.
  let lastGood: AwsSession | null = null
  let seenFirst = false

  // Single-flight: el dashboard dispara ~20 requests en paralelo y el socket de
  // Realtime reintenta aparte; sin esto, cada uno llamaba a getSession() por su
  // cuenta y, en el minuto en que el token expira, TODOS salen a refrescar a la
  // vez con el mismo refresh token. Esa estampida es justo lo que hace que
  // Cognito responda 429 y que un refresh perfectamente válido devuelva error.
  let inFlight: Promise<SessionRead> | null = null

  const readSessionShared = (timeoutMs?: number): Promise<SessionRead> => {
    if (inFlight) return inFlight
    inFlight = readSession(timeoutMs).finally(() => { inFlight = null })
    return inFlight
  }

  // Refresco FORZADO: no espera a que el token venza. Lo usa el transporte
  // cuando el BE/PostgREST responde 401 con un token en mano — la única lectura
  // honesta de ese 401 es "el token que traigo ya no sirve". Va single-flight
  // por lo mismo que la lectura (G-273): un dashboard dispara ~20 requests a la
  // vez y, sin compartir la promesa, un 401 se volvería una estampida de
  // refrescos con el mismo refresh token, que es justo lo que hace que Cognito
  // conteste 429. Lleva tope de tiempo: `refreshSession` es callback-based y un
  // cuelgue dejaría la promesa compartida sin resolver para siempre.
  let refreshInFlight: Promise<AwsSession | null> | null = null

  const runRefresh = (): Promise<AwsSession | null> => new Promise((resolve) => {
    const u = pool.getCurrentUser()
    if (!u) return resolve(null)

    let settled = false
    const done = (s: AwsSession | null) => { if (!settled) { settled = true; resolve(s) } }
    const timer = setTimeout(() => done(null), READ_TIMEOUT_MS)

    u.getSession((err: Error | null, cs: CognitoUserSession | null) => {
      if (err || !cs) { clearTimeout(timer); return done(null) }
      u.refreshSession(cs.getRefreshToken(), (rErr: Error | null, refreshed: CognitoUserSession | null) => {
        clearTimeout(timer)
        if (rErr || !refreshed) return done(null)
        const session = toSession(refreshed)
        const prevToken = lastGood?.access_token ?? null
        lastGood = session
        seenFirst = true
        // Mismo contrato que `currentSession` (G-273): quien copió el token a
        // otro lado —apikey del WS, estado del módulo— se entera por el evento.
        if (session.access_token !== prevToken) emit('TOKEN_REFRESHED', session)
        done(session)
      })
    })
  })

  /**
   * `join = true` (transporte): comparte el refresco en curso. Cualquier token
   * nuevo sirve, y compartir es justo el punto — un dashboard dispara ~20
   * requests a la vez y sin esto un 401 se volvería una estampida de refrescos
   * con el mismo refresh token, que es lo que hace que Cognito conteste 429.
   *
   * `join = false` (impersonación): el llamador acaba de cambiar un claim en el
   * servidor y necesita un token emitido DESPUÉS de ese cambio. Unirse a un
   * refresco que arrancó antes le devolvería el claim viejo —impersonar sin
   * impersonar, en silencio— así que se encola detrás y corre el suyo.
   */
  const forceRefresh = (join = true): Promise<AwsSession | null> => {
    if (join && refreshInFlight) return refreshInFlight
    const previous = refreshInFlight
    const p: Promise<AwsSession | null> = (previous ? previous.then(runRefresh, runRefresh) : runRefresh())
      .then((s) => { if (refreshInFlight === p) refreshInFlight = null; return s })
    refreshInFlight = p
    return p
  }

  /**
   * getSession() en el CognitoUser refresca de forma transparente con el refresh
   * token cuando el id/access token expiró — nuestro "keep me logged in".
   *
   * Ese refresh es SILENCIOSO, y ahí estaba el bug de fondo: quien mantiene el
   * apikey del WebSocket y el `sessionState` del módulo es `onAuthStateChange`,
   * así que si el token se renueva sin avisar, esos dos se quedan clavados en el
   * token del arranque. Medido en los access logs del ALB (2026-08-27): 1442 de
   * 1447 handshakes de `/realtime/v1/websocket` llevaban un apikey YA EXPIRADO
   * (hasta 561 min de retraso) y el ALB devolvía 403; los 5 handshakes con token
   * fresco dieron 101. De ahí el bucle de reconexión infinito.
   *
   * Por eso ahora se emite TOKEN_REFRESHED cuando el token efectivamente cambia.
   */
  const currentSession = async (): Promise<AwsSession | null> => {
    let { session, transient } = await readSessionShared()

    // ARRANQUE: aquí `lastGood` todavía es null, así que la red de seguridad de
    // "conservar la última sesión buena" no tiene nada que conservar y un fallo
    // transitorio se vería como "no hay sesión" — con los tokens de Cognito
    // intactos en disco, el middleware mandaría al login y un simple F5 lo
    // arreglaría. Mientras HAYA usuario guardado, reintentar con un tope corto
    // antes de rendirse. G-277.
    for (let i = 0; transient && !seenFirst && !lastGood && pool.getCurrentUser() && i < BOOT_RETRIES; i++) {
      await new Promise(r => setTimeout(r, BOOT_RETRY_DELAY_MS))
      ;({ session, transient } = await readSessionShared(BOOT_RETRY_TIMEOUT_MS))
    }

    // Fallo transitorio: conservamos la última sesión buena. El caller que
    // necesite un token verá el de antes (o null si nunca hubo uno), pero NADIE
    // interpreta esto como "se cerró la sesión".
    if (transient) return lastGood

    const prevToken = lastGood?.access_token ?? null
    lastGood = session

    if (session && seenFirst && session.access_token !== prevToken) {
      // El token se renovó por debajo. Avisar para que el apikey del WS y el
      // estado del módulo se muevan con él.
      emit('TOKEN_REFRESHED', session)
    }
    seenFirst = true

    return session
  }

  return {
    // ——— consumed by the facade's fetch ———
    async getValidIdToken (): Promise<string | null> {
      const s = await currentSession()
      return s?.access_token ?? null
    },

    // Refresco forzado para el reintento de un 401 (G-277). Devuelve el token
    // nuevo, o null si no se pudo renovar — el caller deja pasar el 401 tal cual,
    // NUNCA cierra la sesión por esto.
    async refreshIdToken (): Promise<string | null> {
      const s = await forceRefresh()
      return s?.access_token ?? null
    },

    // ——— supabase.auth surface ———
    async signInWithPassword ({ email, password }: { email: string, password: string }) {
      return new Promise((resolve) => {
        const user = new CognitoUser({ Username: email, Pool: pool })
        user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
          onSuccess: (cs) => {
            const session = toSession(cs)
            lastGood = session
            seenFirst = true
            emit('SIGNED_IN', session)
            resolve({ data: { user: session.user, session }, error: null })
          },
          onFailure: (err) => resolve({ data: { user: null, session: null }, error: err }),
          newPasswordRequired: () =>
            resolve({ data: { user: null, session: null }, error: new Error('Cambio de contraseña requerido — contacta al administrador') }),
        })
      })
    },

    async signOut () {
      pool.getCurrentUser()?.signOut()
      purgeResidualSessions(opts.clientId)
      // Sin esto, un `currentSession()` posterior que fallara por red devolvería
      // la sesión "buena" anterior — resucitando al usuario que acaba de salir.
      lastGood = null
      seenFirst = true
      emit('SIGNED_OUT', null)
      return { error: null }
    },

    async getSession () {
      return { data: { session: await currentSession() }, error: null }
    },

    async getUser () {
      const s = await currentSession()
      return s
        ? { data: { user: s.user }, error: null }
        : { data: { user: null }, error: new Error('No session') }
    },

    // Un solo camino de refresco forzado (G-277): comparte el tope y la cola de
    // `forceRefresh` en vez de abrir su propia carrera contra el mismo refresh
    // token. Va SIN unirse al refresco en curso (`join = false`): quien llama a
    // esto explícitamente —`stores/admin_impersonation.ts`— acaba de cambiar un
    // claim en el servidor y necesita un token emitido después de ese cambio.
    async refreshSession () {
      const session = await forceRefresh(false)
      return session
        ? { data: { session, user: session.user }, error: null }
        : { data: { session: null, user: null }, error: new Error('Refresh failed') }
    },

    onAuthStateChange (cb: AuthChangeCb) {
      listeners.add(cb)
      currentSession().then(s => cb('INITIAL_SESSION', s))
      return { data: { subscription: { unsubscribe: () => listeners.delete(cb) } } }
    },

    // AWS mode: recovery link via the public forgot-password Lambda (branded
    // Resend email → /auth/set-password?activation=…). NOT Cognito's native
    // forgotPassword: the pool uses COGNITO_DEFAULT email (6-digit code from
    // no-reply@verificationemail.com) and the app has no code-entry UI.
    async resetPasswordForEmail (email: string) {
      if (!opts.functionsUrl) return UNSUPPORTED('resetPasswordForEmail')
      try {
        const res = await fetch(`${opts.functionsUrl}/functions/v1/forgot-password`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email }),
        })
        if (!res.ok) return { data: {}, error: new Error(`No se pudo enviar el correo de recuperación (${res.status})`) }
        return { data: {}, error: null }
      } catch (e: any) {
        return { data: {}, error: e }
      }
    },

    // Recovery-confirm and OTP flows differ in Cognito (code + new password in
    // one step) — pages adapt at the real staging cutover (post-F5).
    async updateUser (_attrs: Record<string, unknown>) { return UNSUPPORTED('updateUser') },
    async verifyOtp (_args: unknown) { return UNSUPPORTED('verifyOtp') },
    async exchangeCodeForSession (_code: string) { return UNSUPPORTED('exchangeCodeForSession') },
    async setSession (_s: unknown) { return UNSUPPORTED('setSession') },
  }
}

export type CognitoAuthAdapter = ReturnType<typeof createCognitoAuth>
